import { useCrawlProgress, type CrawlProgressStatus } from '@/lib/use-crawl-progress';

// Sticky live-progress strip for an in-flight crawl. Mounts above the
// page's normal content; the SSE hook closes itself once the crawl
// reaches a terminal status, after which the strip falls back to the
// last snapshot it received.
export function CrawlProgressStrip({ crawlId }: { crawlId: string }) {
  const progress = useCrawlProgress(crawlId);
  const { status, pagesCrawled, pagesDiscovered, pagesErrored, isStreaming } =
    progress;

  const total = Math.max(pagesDiscovered, pagesCrawled);
  const ratio = total > 0 ? Math.min(1, pagesCrawled / total) : 0;
  const indeterminate =
    isStreaming && (status === 'pending' || (status === 'running' && total === 0));

  return (
    <div
      className="sticky top-0 z-20 flex flex-col gap-1 border-b bg-background/95 px-4 py-2 backdrop-blur"
      data-testid="crawl-progress-strip"
    >
      <div className="flex items-center gap-3 text-xs">
        <StatusPill status={status} />
        <span className="text-muted-foreground">
          {total > 0
            ? `${pagesCrawled} of ${total} pages`
            : isStreaming
              ? 'Discovering pages…'
              : '—'}
        </span>
        {pagesErrored > 0 && (
          <span className="text-rose-700">{pagesErrored} errored</span>
        )}
        {isStreaming && (
          <span className="ml-auto text-[11px] text-muted-foreground">
            live
          </span>
        )}
      </div>
      <ProgressBar ratio={ratio} indeterminate={indeterminate} status={status} />
    </div>
  );
}

function StatusPill({ status }: { status: CrawlProgressStatus }) {
  const tone: Record<CrawlProgressStatus, string> = {
    pending: 'bg-muted text-muted-foreground border-muted',
    running: 'bg-sky-100 text-sky-800 border-sky-300',
    completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    failed: 'bg-rose-100 text-rose-800 border-rose-300',
    cancelled: 'bg-amber-100 text-amber-800 border-amber-300',
    unknown: 'bg-muted text-muted-foreground border-muted',
  };
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] uppercase tracking-wide ${tone[status]}`}
    >
      {status}
    </span>
  );
}

function ProgressBar({
  ratio,
  indeterminate,
  status,
}: {
  ratio: number;
  indeterminate: boolean;
  status: CrawlProgressStatus;
}) {
  const barColour =
    status === 'failed'
      ? 'bg-rose-500'
      : status === 'cancelled'
        ? 'bg-amber-500'
        : status === 'completed'
          ? 'bg-emerald-500'
          : 'bg-sky-500';
  return (
    <div className="h-1 w-full overflow-hidden rounded bg-muted">
      {indeterminate ? (
        <div className={`h-full w-1/3 animate-pulse ${barColour}`} />
      ) : (
        <div
          className={`h-full transition-[width] duration-500 ease-out ${barColour}`}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      )}
    </div>
  );
}
